import { useEffect, useState } from 'react';

import { Clock, Info, RotateCcw, Save, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import {
  CATALOGO_DE_SLAS,
  CHAVES,
  SLAS_PADRAO,
  useConfiguracao,
  type ChaveDeSla,
  type SlasInstitucionais,
} from './dados';

const DIAS_MINIMO = 1;
const DIAS_MAXIMO = 60;

function limitar(valor: number, padrao: number): number {
  if (!Number.isFinite(valor)) {
    return padrao;
  }
  return Math.max(DIAS_MINIMO, Math.min(DIAS_MAXIMO, Math.trunc(valor)));
}

/**
 * `/admin/configuracoes/slas` — os quatro prazos institucionais da
 * apuração mensal, em dias úteis, iguais para todas as DREs.
 *
 * Os valores padrão foram confirmados pelo cliente em 11/06/2026 — são fato de
 * negócio, não dado inventado (ver `dados.ts`, `CATALOGO_DE_SLAS`).
 *
 * "SALVAR ALTERAÇÕES" NÃO GRAVA. `/configuracoes` não existe na API e a tabela
 * `configuracoes` não tem política de escrita. O botão só habilita quando há
 * mudança de fato, valida a faixa, e então mostra o aviso honesto de
 * indisponibilidade (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 * "Restaurar padrão" é o único botão que muda algo: o estado local do campo.
 *
 * Vizinha de `parametros/prazos/sistema` (Prazos do sistema), que trata prazos
 * de todas as categorias numa tabela genérica. As duas existem no protótipo e
 * foram portadas como estão; a sobreposição está registrada em `docs/ALVOS.md`
 * para ser resolvida quando a API de configuração existir.
 */
export function SlasInstitucionaisPage() {
  const consulta = useConfiguracao<SlasInstitucionais>(CHAVES.slasInstitucionais);

  const [valores, setValores] = useState<SlasInstitucionais>(SLAS_PADRAO);
  const [alterados, setAlterados] = useState<Partial<Record<ChaveDeSla, true>>>({});

  useEffect(() => {
    if (consulta.data) {
      setValores(consulta.data);
      setAlterados({});
    }
  }, [consulta.data]);

  const temMudanca = Object.keys(alterados).length > 0;

  function ajustar(chave: ChaveDeSla, bruto: number, padrao: number) {
    // A asserção é por causa da chave computada de tipo união: o TypeScript
    // alarga `{ ...atual, [chave]: ... }` e perde a forma exata do registro.
    setValores((atual) => ({ ...atual, [chave]: limitar(bruto, padrao) }) as SlasInstitucionais);
    setAlterados((atual) => ({ ...atual, [chave]: true }));
  }

  function salvar() {
    const foraDaFaixa = CATALOGO_DE_SLAS.some((definicao) => {
      const valor = valores[definicao.chave];
      return !Number.isInteger(valor) || valor < DIAS_MINIMO || valor > DIAS_MAXIMO;
    });
    if (foraDaFaixa) {
      toast.error('Prazo inválido', {
        description: `Todo prazo precisa ficar entre ${DIAS_MINIMO} e ${DIAS_MAXIMO} dias úteis.`,
      });
      return;
    }
    // A tabela `configuracoes` não aceita escrita hoje. O aviso é honesto: nada
    // foi gravado, então os campos alterados continuam marcados como alterados.
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="SLAs institucionais"
        description="Prazos oficiais da apuração mensal, em dias úteis, iguais para todas as DREs. Aceita de 1 a 60 dias."
      />

      {consulta.isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar os SLAs agora. Verifique sua conexão e tente novamente."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      ) : consulta.isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {CATALOGO_DE_SLAS.map((definicao) => (
              <Card key={definicao.chave}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
                    {definicao.titulo}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">{definicao.descricao}</p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`sla-${definicao.chave}`} className="text-xs">
                        Prazo (dias úteis)
                      </Label>
                      <Input
                        id={`sla-${definicao.chave}`}
                        type="number"
                        min={DIAS_MINIMO}
                        max={DIAS_MAXIMO}
                        className="w-28"
                        value={valores[definicao.chave]}
                        onChange={(evento) =>
                          ajustar(definicao.chave, Number(evento.target.value), definicao.padrao)
                        }
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => ajustar(definicao.chave, definicao.padrao, definicao.padrao)}
                    >
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      Padrão ({definicao.padrao})
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={salvar} disabled={!temMudanca}>
              <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Salvar alterações
            </Button>
          </div>

          <Alert>
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Prazos de regra fixa, que não se configuram aqui</AlertTitle>
            <AlertDescription className="space-y-1.5 text-xs">
              <div>
                <strong>Resposta da SME ao DTP, no bloqueio de pagamento:</strong> não tem prazo. O
                termo fica bloqueado até a SME responder.
              </div>
              <div>
                <strong>Revisão de competência já encerrada:</strong> permitida apenas dentro do
                ano-calendário vigente, preferencialmente depois do envio do demonstrativo.
              </div>
            </AlertDescription>
          </Alert>

          <Alert>
            <ShieldAlert className="h-4 w-4 text-warning" aria-hidden="true" />
            <AlertTitle>Efeito de uma alteração</AlertTitle>
            <AlertDescription className="text-xs">
              O novo prazo vale para os cálculos feitos a partir da mudança — indicadores de prazo,
              cartões de demanda e cobranças da SME para a DRE. Prazos já em curso não são
              recontados.
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}

export default SlasInstitucionaisPage;
